import React, { useState, useEffect } from 'react'
import { supabase } from '../../supabase'

const inputStyle = {
  background: 'var(--input-bg)', border: '1px solid var(--border)',
  borderRadius: '4px', padding: '0.55rem 0.75rem',
  fontSize: '0.88rem', color: 'var(--text)',
  width: '100%', boxSizing: 'border-box', outline: 'none'
}

const labelStyle = {
  display: 'block', fontFamily: 'monospace',
  fontSize: '0.67rem', fontWeight: '500',
  textTransform: 'uppercase', letterSpacing: '0.08em',
  color: 'var(--text-muted)', marginBottom: '0.35rem'
}

export default function AdminSettings({ session, onSettingsSaved }) {
  const [settings, setSettings] = useState({
    app_name: 'DebtCrush',
    app_logo_url: '',
    primary_color: '#c9a84c',
    support_email: '',
    support_phone: '',
    play_store_url: 'https://play.google.com/store/apps/details?id=com.debtcrush.app',
  })
  const [loading, setLoading] = useState(true)
  const [saving, setSaving] = useState(false)
  const [toast, setToast] = useState('')

  useEffect(() => { fetchSettings() }, [])

  function showToast(msg) {
    setToast(msg)
    setTimeout(() => setToast(''), 3000)
  }

  async function fetchSettings() {
    const { data } = await supabase
      .from('app_settings')
      .select('*')
      .eq('id', 1)
      .single()
    if (data) setSettings(prev => ({ ...prev, ...data }))
    setLoading(false)
  }

  async function saveSettings() {
    setSaving(true)
    const { error } = await supabase
      .from('app_settings')
      .upsert({
        id: 1,
        app_name: settings.app_name,
        app_logo_url: settings.app_logo_url,
        primary_color: settings.primary_color,
        support_email: settings.support_email,
        support_phone: settings.support_phone
      })
      .eq('id', 1)

    if (error) {
      console.error('Settings error:', error.message)
      showToast('❌ Error: ' + error.message)
    } else {
      showToast('✅ Settings saved successfully!')
if (onSettingsSaved) onSettingsSaved()
    }
    setSaving(false)
  }

  function set(field, value) {
    setSettings(prev => ({ ...prev, [field]: value }))
  }

  if (loading) return (
    <div style={{ textAlign: 'center', padding: '3rem', color: 'var(--text-muted)' }}>Loading settings...</div>
  )

  return (
    <div>
      {/* Toast */}
      {toast && (
        <div style={{
          position: 'fixed', bottom: '1.5rem', right: '1.5rem',
          background: '#0d0d0d', color: '#f5f0e8',
          padding: '0.7rem 1.1rem', borderRadius: '6px',
          fontSize: '0.8rem', zIndex: 9999,
          borderLeft: '3px solid #c9a84c'
        }}>{toast}</div>
      )}

      <div style={{ marginBottom: '1.5rem' }}>
        <h2 style={{ fontFamily: 'Georgia,serif', fontSize: '1.55rem', fontWeight: '700', margin: 0, color: 'var(--text)' }}>App Settings</h2>
        <p style={{ color: 'var(--text-muted)', fontSize: '0.8rem', marginTop: '0.3rem' }}>Control branding and app configuration. Only Super Admin can access this.</p>
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1.5rem' }}>

        {/* Branding */}
        <div style={{ background: 'var(--surface)', border: '1px solid var(--border)', borderRadius: '8px', overflow: 'hidden' }}>
          <div style={{ padding: '1rem 1.4rem', borderBottom: '1px solid var(--border)', background: 'var(--surface-hdr)' }}>
            <span style={{ fontFamily: 'Georgia,serif', fontSize: '1.05rem', fontWeight: '700', color: 'var(--text)' }}>🎨 Branding</span>
          </div>
          <div style={{ padding: '1.4rem', display: 'flex', flexDirection: 'column', gap: '1rem' }}>
            <div>
              <label style={labelStyle}>App Name</label>
              <input style={inputStyle} value={settings.app_name} onChange={e => set('app_name', e.target.value)} placeholder="DebtCrush" />
            </div>
            <div>
              <label style={labelStyle}>Logo URL</label>
              <input style={inputStyle} value={settings.app_logo_url || ''} onChange={e => set('app_logo_url', e.target.value)} placeholder="https://yourdomain.com/logo.png" />
              <div style={{ fontSize: '0.72rem', color: '#8a8278', marginTop: '0.3rem' }}>Upload your logo somewhere and paste the URL here.</div>
            </div>
            <div>
              <label style={labelStyle}>Primary Color</label>
              <div style={{ display: 'flex', gap: '0.5rem', alignItems: 'center' }}>
                <input type="color" value={settings.primary_color} onChange={e => set('primary_color', e.target.value)}
                  style={{ width: '40px', height: '36px', border: '1px solid var(--border)', borderRadius: '4px', cursor: 'pointer', padding: '2px' }} />
                <input style={{ ...inputStyle, flex: 1 }} value={settings.primary_color} onChange={e => set('primary_color', e.target.value)} placeholder="#c9a84c" />
              </div>
            </div>
            {/* Preview */}
            <div style={{ padding: '1rem', background: 'var(--bg)', borderRadius: '6px', border: '1px solid var(--border)' }}>
              <div style={{ fontFamily: 'monospace', fontSize: '0.65rem', color: 'var(--text-muted)', marginBottom: '0.5rem', textTransform: 'uppercase' }}>Preview</div>
              <div style={{ fontFamily: 'Georgia,serif', fontSize: '1.4rem', fontWeight: '900', color: 'var(--text)' }}>
                {settings.app_name.split(' ')[0]} <span style={{ color: settings.primary_color, fontStyle: 'italic' }}>{settings.app_name.split(' ')[1] || ''}</span>
              </div>
            </div>
          </div>
        </div>

        {/* Support & Contact */}
        <div style={{ background: 'var(--surface)', border: '1px solid var(--border)', borderRadius: '8px', overflow: 'hidden' }}>
          <div style={{ padding: '1rem 1.4rem', borderBottom: '1px solid var(--border)', background: 'var(--surface-hdr)' }}>
            <span style={{ fontFamily: 'Georgia,serif', fontSize: '1.05rem', fontWeight: '700', color: 'var(--text)' }}>📞 Support & Contact</span>
          </div>
          <div style={{ padding: '1.4rem', display: 'flex', flexDirection: 'column', gap: '1rem' }}>
            <div>
              <label style={labelStyle}>Support Email</label>
              <input style={inputStyle} type="email" value={settings.support_email || ''} onChange={e => set('support_email', e.target.value)} placeholder="support@debtcrush.in" />
            </div>
            <div>
              <label style={labelStyle}>Support Phone</label>
              <input style={inputStyle} type="tel" value={settings.support_phone || ''} onChange={e => set('support_phone', e.target.value)} placeholder="+91XXXXXXXXXX" />
            </div>
            <div>
              <label style={labelStyle}>Play Store URL</label>
              <input style={inputStyle} value={settings.play_store_url || ''} onChange={e => set('play_store_url', e.target.value)} placeholder="https://play.google.com/store/apps/details?id=com.debtcrush.app" />
              <div style={{ fontSize: '0.72rem', color: '#8a8278', marginTop: '0.3rem' }}>Android app link shown on Login page and Dashboard footer.</div>
            </div>
            {/* Domain info */}
            <div style={{ padding: '1rem', background: 'rgba(201,168,76,0.06)', borderRadius: '6px', border: '1px solid rgba(201,168,76,0.2)' }}>
              <div style={{ fontFamily: 'monospace', fontSize: '0.65rem', color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: '0.08em', marginBottom: '0.5rem' }}>Domain Info</div>
              <div style={{ fontSize: '0.82rem', color: 'var(--text)', display: 'flex', flexDirection: 'column', gap: '0.3rem' }}>
                <div>🌐 Main App: <strong>debtcrush.in</strong></div>
                <div>🔧 Admin: <strong>debtcrush.in/admin</strong></div>
                <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                  📱 Android: &nbsp;
                  <a href={settings.play_store_url || 'https://play.google.com/store/apps/details?id=com.debtcrush.app'} target="_blank" rel="noopener noreferrer" style={{ color: '#c9a84c', fontWeight: '700', fontSize: '0.8rem' }}>
                    View on Play Store ↗
                  </a>
                </div>
              </div>
            </div>
          </div>
        </div>

      </div>

      {/* Save Button */}
      <div style={{ marginTop: '1.5rem', display: 'flex', justifyContent: 'flex-end' }}>
        <button
          onClick={saveSettings}
          disabled={saving}
          style={{
            background: '#0d0d0d', color: '#f5f0e8', border: 'none',
            padding: '0.65rem 1.5rem', borderRadius: '6px',
            fontFamily: 'monospace', fontSize: '0.78rem',
            fontWeight: '500', cursor: 'pointer',
            textTransform: 'uppercase', letterSpacing: '0.05em'
          }}
        >
          {saving ? 'Saving...' : '💾 Save Settings'}
        </button>
      </div>
    </div>
  )
}